import { RegressionNode } from '../../../../types/RegressionNode';
import { useNodeOperations } from '../../../../contexts/NodeOperationsContext';
import { useState, useEffect } from 'react';
import { reinstantiate } from '../../../../utils/nodeFactory';
import "./ComponentCard.css";

interface Props {
    node: RegressionNode;
}

export function RegressionCard({ node }: Props) {
    const { onUpdate, isInteractionLocked } = useNodeOperations();
    const reinstantiatedNode = reinstantiate(node) as RegressionNode;
    const liveNode = typeof reinstantiatedNode?.update === 'function'
        ? reinstantiatedNode
        : new RegressionNode(
            node.id,
            node.title,
            node.config?.model_type,
            node.config?.weights,
            node.config?.inputs,
            node.isManualRename,
            node.config?.last_calibrated_at,
            node.config?.calibration_artifact_id,
            node.config?.calibration_artifact_path,
            node.config?.feature_tree_hash,
            node.config?.feature_labels
        );
    const { config } = liveNode;

    const [localWeights, setLocalWeights] = useState(config.weights.map(String));

    useEffect(() => {
        setLocalWeights(config.weights.map(String));
    }, [config]);

    const featureLabels = config.feature_labels ?? [];
    const featureCount = featureLabels.length || config.weights.length;

    return (
        <div className="internal-card-container" style={{ minWidth: '320px' }}>
            <div className="flex flex-col gap-sm">
                <div className="flex items-center justify-between">
                    <label className="field-label">Model Type</label>
                    <select 
                        className="select-premium nodrag"
                        value={config.model_type}
                        onChange={e => onUpdate(liveNode.id, liveNode.update(liveNode.id, { config: { ...config, model_type: e.target.value as any } }))}
                        disabled={isInteractionLocked}
                    >
                        <option value="ridge">Ridge Regression</option>
                        <option value="linear">Linear Regression</option>
                    </select>
                </div>
                {config.last_calibrated_at && (
                    <div style={{ 
                        marginTop: '0.4rem', 
                        padding: '4px 8px', 
                        background: 'rgba(0, 255, 255, 0.05)', 
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 255, 255, 0.1)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '0.65rem'
                    }}>
                        <span style={{ color: 'var(--meep-cyan)', opacity: 0.8 }}>Calibrated</span>
                        <span style={{ opacity: 0.6 }}>{new Date(config.last_calibrated_at).toLocaleDateString()} {new Date(config.last_calibrated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                )}

                {config.calibration_artifact_path && (
                    <div style={{
                        marginTop: '0.4rem',
                        padding: '6px 8px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '4px',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                        fontSize: '0.65rem'
                    }}>
                        <div style={{ color: 'var(--text-muted)', marginBottom: '2px' }}>Artifact</div>
                        <div style={{ opacity: 0.7, wordBreak: 'break-all' }}>{config.calibration_artifact_path}</div>
                    </div>
                )}

                <div className="regression-weights-list" style={{ marginTop: '0.5rem' }}>
                    <div className="flex justify-between items-center mb-xs">
                        <label className="field-label" style={{ opacity: 0.7 }}>Feature Weights (Beta)</label>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                            {config.inputs.length} blocks / {featureCount} weights
                        </span>
                    </div>
                    
                    {config.inputs.length === 0 ? (
                        <div style={{ fontSize: '0.8rem', fontStyle: 'italic', opacity: 0.5, textAlign: 'center', padding: '1rem', border: '1px dashed var(--surface-glass-border)', borderRadius: '4px' }}>
                            Connect and drop features below to enable fitting
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {config.weights.map((weight, i) => (
                                <div key={`weight-${i}`} style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: '8px', alignItems: 'center', background: 'var(--surface-glass-highlight)', padding: '4px 8px', borderRadius: '4px' }}>
                                    <span style={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {featureLabels[i] || `Feature ${i + 1}`}
                                    </span>
                                    <input
                                        type="number"
                                        step="0.00001"
                                        className="input-number-premium pan-zoom-exclude nodrag"
                                        style={{ padding: '2px 4px', fontSize: '0.8rem', opacity: 1 }}
                                        value={localWeights[i] || String(weight ?? 0)}
                                        onChange={e => {
                                            const val = e.target.value;
                                            const newWeightsStr = [...localWeights];
                                            newWeightsStr[i] = val;
                                            setLocalWeights(newWeightsStr);
                                            
                                            const newWeightsNum = [...config.weights];
                                            newWeightsNum[i] = Number(val);
                                            onUpdate(liveNode.id, liveNode.update(liveNode.id, { config: { ...config, weights: newWeightsNum } }));
                                        }}
                                        disabled={isInteractionLocked}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
